import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";
import { StaticImageData } from "next/image";

import { Language } from "@/Apollo/schema";
import AndroidDownloadDEImage from "@/common/components/assets/app-download-block-android.de.png";
import AndroidDownloadENImage from "@/common/components/assets/app-download-block-android.en.png";
import AndroidDownloadESImage from "@/common/components/assets/app-download-block-android.es.png";
import AndroidDownloadFRImage from "@/common/components/assets/app-download-block-android.fr.png";
import AndroidDownloadITImage from "@/common/components/assets/app-download-block-android.it.png";
import AndroidDownloadNLImage from "@/common/components/assets/app-download-block-android.nl.png";
import AppleDownloadDEImage from "@/common/components/assets/app-download-block-apple.de.png";
import AppleDownloadENImage from "@/common/components/assets/app-download-block-apple.en.png";
import AppleDownloadESImage from "@/common/components/assets/app-download-block-apple.es.png";
import AppleDownloadFRImage from "@/common/components/assets/app-download-block-apple.fr.png";
import AppleDownloadITImage from "@/common/components/assets/app-download-block-apple.it.png";
import AppleDownloadNLImage from "@/common/components/assets/app-download-block-apple.nl.png";
import { useStoreLinks } from "@/common/hooks/apps.hooks";
import { useMakeInteractive } from "@/common/hooks/html.hooks";
import { useLanguage } from "@/common/hooks/pages.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import Image from "@/components/common/Image";

const IOS_BUTTONS: Record<Language, StaticImageData> = {
  [Language.En]: AppleDownloadENImage,
  [Language.Nl]: AppleDownloadNLImage,
  [Language.De]: AppleDownloadDEImage,
  [Language.Fr]: AppleDownloadFRImage,
  [Language.Es]: AppleDownloadESImage,
  [Language.It]: AppleDownloadITImage,
};

const ANDROID_BUTTONS: Record<Language, StaticImageData> = {
  [Language.En]: AndroidDownloadENImage,
  [Language.Nl]: AndroidDownloadNLImage,
  [Language.De]: AndroidDownloadDEImage,
  [Language.Fr]: AndroidDownloadFRImage,
  [Language.Es]: AndroidDownloadESImage,
  [Language.It]: AndroidDownloadITImage,
};

export type Device = "ios" | "android";

type HomepageAppDownloadButtonProps = {
  device: Device;
  analyticsSource?: string;
  className?: string;
  onClick?: (device: Device) => void;
};

/**
 * A store badge. The link is derived from the page locale here rather than
 * passed in, so no caller can point a badge at a fixed storefront again
 * (WHO-5649).
 */
export const AppDownloadButton = ({
  device,
  analyticsSource,
  className,
  onClick,
}: HomepageAppDownloadButtonProps) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { ios, android } = useStoreLinks();
  const { makeInteractive } = useMakeInteractive("a");
  const storeName = device === "ios" ? "App Store" : "Google Play";
  const href = device === "ios" ? ios : android;

  const imageSrc =
    device === "ios" ? IOS_BUTTONS[language] : ANDROID_BUTTONS[language];

  const handleDownloadClick = (device: Device) => () => {
    analyticsService.trackClickedAppInstall(device, analyticsSource);
    onClick?.(device);
  };

  return (
    // The sizing/positioning lives on the <a> itself so the `fill` image has a
    // positioned parent (`block` because an <a> is inline by default).
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={cn("relative block h-12 w-36 md:h-8 md:w-24", className)}
      // The badge is an image of text, so the link names itself in the page
      // locale and the image is decorative (WCAG 2.4.4, WHO-5550).
      aria-label={`${t("common:common-download-app-title")}: ${storeName}`}
      {...makeInteractive(handleDownloadClick(device))}
    >
      <Image src={imageSrc} layout="fill" alt="" />
    </Link>
  );
};
